/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  RejectWithdrawalOptions,
  RejectWithdrawalStatus200,
  RejectWithdrawalStatus400,
  RejectWithdrawalStatus401,
  RejectWithdrawalStatus403,
  RejectWithdrawalStatus404,
  RejectWithdrawalStatus409,
  RejectWithdrawalStatus500,
} from '../types/RejectWithdrawal'
import type { UseMutationOptions, UseMutationResult, QueryClient } from '@tanstack/react-query'
import { rejectWithdrawal } from '../clients/rejectWithdrawal'
import { mutationOptions, useMutation } from '@tanstack/react-query'

export const rejectWithdrawalMutationKey = () =>
  [{ url: '/v1/web3/admin/withdrawals/:withdrawal_id/reject' }] as const

export function rejectWithdrawalMutationOptions<TContext = unknown>(
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const mutationKey = rejectWithdrawalMutationKey()
  return mutationOptions<
    RejectWithdrawalStatus200,
    ResponseErrorConfig<
      | RejectWithdrawalStatus400
      | RejectWithdrawalStatus401
      | RejectWithdrawalStatus403
      | RejectWithdrawalStatus404
      | RejectWithdrawalStatus409
      | RejectWithdrawalStatus500
    >,
    RejectWithdrawalOptions,
    TContext
  >({
    mutationKey,
    mutationFn: async ({ path, body }) => {
      return rejectWithdrawal({ ...config, path, body, throwOnError: true }).unwrap()
    },
  })
}

/**
 * @description Rejects a withdrawal that no fee has been locked against, transitioning it to REJECTED and releasing its reservation: the reserved quantity+fee is credited back to the user's available balance. Nothing is sent on-chain. The supplied reason is recorded as the withdrawal's failure_reason.
 * Both `PENDING` and `APPROVED_WITHOUT_FEE` withdrawals may be rejected — an approval that has not been paid for is still reversible. A rejected withdrawal keeps its approved_by/approved_at if it had them. `APPROVED` and `BROADCAST` withdrawals may not: a locked fee means the withdrawal is queued for submission, so those are refused with 409 and can only end in FAILED. A reservation can never be released twice.
 * @summary Reject a USDC withdrawal that has no fee locked (admin)
 * {@link /v1/web3/admin/withdrawals/:withdrawal_id/reject}
 */
export function useRejectWithdrawal<TContext>(
  options: {
    mutation?: UseMutationOptions<
      RejectWithdrawalStatus200,
      ResponseErrorConfig<
        | RejectWithdrawalStatus400
        | RejectWithdrawalStatus401
        | RejectWithdrawalStatus403
        | RejectWithdrawalStatus404
        | RejectWithdrawalStatus409
        | RejectWithdrawalStatus500
      >,
      RejectWithdrawalOptions,
      TContext
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { mutation = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...mutationOptions } = mutation
  const mutationKey = mutationOptions.mutationKey ?? rejectWithdrawalMutationKey()

  const baseOptions = rejectWithdrawalMutationOptions(config) as UseMutationOptions<
    RejectWithdrawalStatus200,
    ResponseErrorConfig<
      | RejectWithdrawalStatus400
      | RejectWithdrawalStatus401
      | RejectWithdrawalStatus403
      | RejectWithdrawalStatus404
      | RejectWithdrawalStatus409
      | RejectWithdrawalStatus500
    >,
    RejectWithdrawalOptions,
    TContext
  >

  return useMutation<
    RejectWithdrawalStatus200,
    ResponseErrorConfig<
      | RejectWithdrawalStatus400
      | RejectWithdrawalStatus401
      | RejectWithdrawalStatus403
      | RejectWithdrawalStatus404
      | RejectWithdrawalStatus409
      | RejectWithdrawalStatus500
    >,
    RejectWithdrawalOptions,
    TContext
  >(
    {
      ...baseOptions,
      mutationKey,
      ...mutationOptions,
    },
    queryClient,
  ) as UseMutationResult<
    RejectWithdrawalStatus200,
    ResponseErrorConfig<
      | RejectWithdrawalStatus400
      | RejectWithdrawalStatus401
      | RejectWithdrawalStatus403
      | RejectWithdrawalStatus404
      | RejectWithdrawalStatus409
      | RejectWithdrawalStatus500
    >,
    RejectWithdrawalOptions,
    TContext
  >
}
